/**
 * CustomerFilterBar - 고객 목록 필터 바
 *
 * 검색, 정렬, 즐겨찾기 토글 통합 필터 UI
 */

import { Search, Star, ArrowUpDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { CustomerSortOrder } from "@/hooks/useCustomerFilters";

interface CustomerFilterBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortOrder: CustomerSortOrder;
  onSortChange: (sort: CustomerSortOrder) => void;
  starred: boolean | undefined;
  onStarredToggle: () => void;
  sortOptions: { value: CustomerSortOrder; label: string }[];
}

export function CustomerFilterBar({
  searchQuery,
  onSearchChange,
  sortOrder,
  onSortChange,
  starred,
  onStarredToggle,
  sortOptions,
}: CustomerFilterBarProps) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 px-4 pb-4">
      {/* 검색 */}
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="이름, 전화번호, 태그 검색..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* 정렬 */}
      <Select
        value={sortOrder}
        onValueChange={(v) => onSortChange(v as CustomerSortOrder)}
      >
        <SelectTrigger className="w-full sm:w-[180px]">
          <ArrowUpDown className="w-4 h-4 mr-2 text-muted-foreground sm:hidden" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {sortOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* 즐겨찾기 토글 */}
      <Button
        variant={starred ? "default" : "outline"}
        onClick={onStarredToggle}
        className="w-full sm:w-auto"
      >
        <Star className={cn("w-4 h-4 mr-2", starred && "fill-current")} />
        즐겨찾기
      </Button>
    </div>
  );
}
